'use client'

import { useState } from 'react'
import Image from 'next/image'
import { useSession } from 'next-auth/react'

import { reportSentryError } from 'lib/sentry'
import { ProductSeoStorage } from 'shared/analytics'
import Button from 'shared/ui/Button'
import ExpandablePanel from 'shared/ui/ExpandablePanel'
import { Modal, ModalTitle } from 'shared/ui/Modal'
import toast from 'shared/ui/utils/toast'
import { noop } from 'shared/utils/common'
import { getApiErrors } from 'shared/utils/errors'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import useCartStore from '../../store/cartStore'
import { ProductsForMerge } from '../../types'

import styles from './MergeCartsModal.module.scss'

export interface MergedCartItemsInterface {
  authCartId?: string
  tempCartDiscountCodeText?: string
  authCartDiscountCodeId?: string
  auth: ProductsForMerge[]
  temp: ProductsForMerge[]
}

interface MergeCartsModalProps {
  open: boolean
  mergedCartItems: MergedCartItemsInterface | null
  onClose: () => void
}

const MergeCartsModal = ({
  open,
  mergedCartItems,
  onClose
}: MergeCartsModalProps) => {
  const {
    addToCart,
    clearCart,
    addDiscountCodeToCart,
    removeDiscountCodeFromCart
  } = useCartStore()
  const [loading, setLoading] = useState(false)
  const [executed, setExecuted] = useState(false)
  const session = useSession()
  const tempCartProducts = mergedCartItems?.temp?.map(
    (prod: ProductsForMerge) => ({
      productId: prod.productId,
      quantity: prod.quantity
    })
  )

  const authCartProducts = mergedCartItems?.auth?.map(
    (prod: ProductsForMerge) => ({
      productId: prod.productId,
      quantity: prod.quantity
    })
  )

  // useEffect(() => {
  //   const handleBeforeUnload = (event) => {
  //     const message = 'Czy na pewno chcesz opuścić tę stronę?'
  //
  //     // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  //     event.returnValue = message
  //
  //     return message
  //   }
  //
  //   window.addEventListener('beforeunload', handleBeforeUnload)
  //
  //   return () => {
  //     window.removeEventListener('beforeunload', handleBeforeUnload)
  //   }
  // }, [])

  const hasTempProducts = isNotNilOrEmpty(tempCartProducts)
  const hasAuthProducts = isNotNilOrEmpty(authCartProducts)
  const hasBoth = hasTempProducts && hasAuthProducts

  const replaceDiscountCode = async () => {
    if (mergedCartItems?.tempCartDiscountCodeText) {
      try {
        if (mergedCartItems?.authCartDiscountCodeId) {
          await removeDiscountCodeFromCart(
            mergedCartItems.authCartDiscountCodeId,
            session,
            mergedCartItems?.authCartId
          )
        }

        await addDiscountCodeToCart(
          mergedCartItems.tempCartDiscountCodeText,
          session,
          mergedCartItems?.authCartId
        )
      } catch (error) {
        console.error('Error with discount codes:', error)
      }
    }
  }

  const merge = async () => {
    setLoading(true)

    try {
      await addToCart(
        {
          items: tempCartProducts || [],
          givenCartId: mergedCartItems?.authCartId
            ? String(mergedCartItems?.authCartId)
            : undefined
        },
        session
      )
      await replaceDiscountCode()

      toast.success('Koszyki zostały połączone')
      onClose()
      setLoading(false)
    } catch (err) {
      setLoading(false)
      toast.error(getApiErrors(err))

      reportSentryError(err, 'Failed to merge carts')
    }
  }

  const leaveTemp = (hideToast?: boolean) => () => {
    setLoading(true)
    clearCart(session, mergedCartItems?.authCartId)
      .then(() => {
        addToCart(
          {
            items: tempCartProducts || [],
            givenCartId: mergedCartItems?.authCartId
          },
          session
        )
          .then(async () => {
            !hideToast && toast.success('Zachowano aktualny koszyk')
            await replaceDiscountCode()
            onClose()
            setLoading(false)
          })
          .catch(err => {
            setLoading(false)
            toast.error(getApiErrors(err))

            reportSentryError(err, 'Failed to leave temp cart')
          })
      })
      .catch(err => {
        setLoading(false)
        toast.error(getApiErrors(err))
      })
  }

  const leaveAuth = () => {
    ProductSeoStorage.clear()
    onClose()
    toast.success('Zachowano koszyk z Twojego konta')
  }

  if (hasBoth && open) {
    return (
      <Modal
        id='merge-carts-modal'
        open={open && hasBoth}
        hideDefaultCloseButton
        closeOnBackdropClick={false}
        closeOnEscape={false}
        onClose={noop}
      >
        <ModalTitle>Łączenie koszyków</ModalTitle>
        {isNotNilOrEmpty(mergedCartItems?.temp) && (
          <ExpandablePanel
            id={`merge_cart_local_cart_list`}
            title='Produkty w aktualnym koszyku'
          >
            {mergedCartItems?.temp?.map((item: ProductsForMerge) => (
              <div
                className={styles.mergeModalProduct}
                id={`merge_cart_local_cart_${item?.name}`}
                key={item.productId}
              >
                <Image width={80} height={80} alt={item.name} src={item.img} />
                <div className={styles.productName}>{item.name}</div>
                <div className={styles.productQuantity}>{item.quantity}</div>
              </div>
            ))}
          </ExpandablePanel>
        )}
        {isNotNilOrEmpty(mergedCartItems?.auth) && (
          <ExpandablePanel
            id={`merge_cart_auth_cart_list`}
            title='Produkty w koszyku na Twoim koncie'
          >
            {mergedCartItems?.auth?.map((item: ProductsForMerge) => (
              <div
                className={styles.mergeModalProduct}
                id={`merge_cart_auth_cart_${item?.name}`}
                key={`auth-${item.name}`}
              >
                <Image width={80} height={80} alt={item.name} src={item.img} />
                <div className={styles.productName}>{item.name}</div>
                <div className={styles.productQuantity}>{item.quantity}</div>
              </div>
            ))}
          </ExpandablePanel>
        )}
        <div className={styles.buttonsWrapper}>
          <Button
            id='merge_cart_button_leave_temp'
            variant='neutral'
            fullWidth
            onClick={leaveTemp(false)}
            disabled={loading}
          >
            Zachowaj aktualny
          </Button>
          <Button
            id='merge_cart_button_leave_auth'
            variant='neutral'
            fullWidth
            onClick={leaveAuth}
            disabled={loading}
          >
            Zachowaj koszyk z Twojego konta
          </Button>
          <Button
            id='merge_cart_button_merge'
            variant='primary'
            fullWidth
            onClick={merge}
            disabled={loading}
          >
            Połącz koszyki
          </Button>
        </div>
      </Modal>
    )
  } else if (hasTempProducts && !hasAuthProducts && open && !executed) {
    leaveTemp(true)()
    setExecuted(true)

    return null
  }

  return null
}

export default MergeCartsModal
